/**
 * eMpowered (course id 4) progress helpers. Do not use from PYP render paths.
 */

/**
 * @param {object} pyp
 * @param {number|string} moduleId
 * @returns {number[]}
 */
export function getOrderedPartIds(pyp, moduleId) {
  const parts =
    pyp?.partsByModuleId?.[moduleId] ||
    pyp?.partsByModuleId?.[String(moduleId)] ||
    []
  return (Array.isArray(parts) ? parts : [])
    .map((p) => Number(p?.id ?? p))
    .filter((id) => !Number.isNaN(id) && id > 0)
}

/**
 * @param {object} pyp
 * @param {number|string} moduleId
 * @returns {number|null}
 */
export function getLastVisitedPartId(pyp, moduleId) {
  const meta =
    pyp?.navigation?.moduleNavigationMetaData?.[moduleId] ||
    pyp?.navigation?.moduleNavigationMetaData?.[String(moduleId)]
  const id = Number(meta?.lastVisitedPart)
  return id > 0 ? id : null
}

/**
 * True when furthest visited part is the last part in the module's ordered list.
 * @param {object} pyp
 * @param {number|string} moduleId
 * @returns {boolean}
 */
export function isEMpoweredModuleFullyProgressed(pyp, moduleId) {
  const ordered = getOrderedPartIds(pyp, moduleId)
  if (!ordered.length) return false
  const lastVisitedPartId = getLastVisitedPartId(pyp, moduleId)
  if (lastVisitedPartId == null) return false
  const lastIdx = ordered.findIndex(
    (id) => Number(id) === Number(lastVisitedPartId)
  )
  return lastIdx >= 0 && lastIdx === ordered.length - 1
}

/**
 * Module N is locked until module N-1's furthest part is the last part.
 * Does not unlock from user_modules.completed alone (avoids stale completed).
 * @param {object} pyp
 * @param {number|string} moduleId
 * @returns {boolean}
 */
export function isEMpoweredModuleLocked(pyp, moduleId) {
  const allIds = pyp?.modules?.allIds || []
  const index = allIds.findIndex((id) => Number(id) === Number(moduleId))
  if (index <= 0) return false
  const previousModuleId = allIds[index - 1]
  return !isEMpoweredModuleFullyProgressed(pyp, previousModuleId)
}

/**
 * @param {object} pyp
 * @param {number|string} moduleId
 * @returns {Array<{ label?: string, partIds?: number[], firstPartId?: number }>}
 */
export function getSubcoursesForModule(pyp, moduleId) {
  return (
    pyp?.subcoursesByModuleId?.[moduleId] ||
    pyp?.subcoursesByModuleId?.[String(moduleId)] ||
    []
  )
}

/**
 * Inside an unlocked module: subcourse 0 is open; later ones open only after
 * lastVisitedPart has reached that subcourse's start (earlier ones revisit-able).
 */
export function isSubcourseLocked({
  orderedPartIds,
  subcourse,
  subcourseIndex,
  lastVisitedPartId,
  moduleLocked
}) {
  if (moduleLocked) return true
  if (subcourseIndex === 0) return false

  const ordered = (orderedPartIds || []).map(Number)
  const lastIdx =
    lastVisitedPartId != null && lastVisitedPartId !== ""
      ? ordered.findIndex((id) => Number(id) === Number(lastVisitedPartId))
      : -1
  if (lastIdx < 0) return true

  const partIds = (subcourse?.partIds || []).map(Number)
  if (!partIds.length) return true

  let minIdx = Infinity
  for (const pid of partIds) {
    const i = ordered.findIndex((id) => Number(id) === Number(pid))
    if (i >= 0 && i < minIdx) minIdx = i
  }
  if (minIdx === Infinity) return true
  return minIdx > lastIdx
}

/**
 * If requested part is in a locked module/subcourse, clamp to lastVisited or first part.
 */
export function resolveSelectablePartId({
  requestedPartId,
  orderedPartIds,
  subcourses,
  lastVisitedPartId,
  moduleLocked
}) {
  const ordered = (orderedPartIds || []).map(Number).filter((id) => id > 0)
  const last =
    lastVisitedPartId != null &&
    ordered.includes(Number(lastVisitedPartId))
      ? Number(lastVisitedPartId)
      : ordered[0] ?? null

  if (moduleLocked) return last

  const req = Number(requestedPartId)
  if (!req || req <= 0 || !ordered.includes(req)) return last

  const list = Array.isArray(subcourses) ? subcourses : []
  for (let j = 0; j < list.length; j++) {
    const partIds = (list[j].partIds || []).map(Number)
    if (!partIds.includes(req)) continue
    if (
      isSubcourseLocked({
        orderedPartIds: ordered,
        subcourse: list[j],
        subcourseIndex: j,
        lastVisitedPartId,
        moduleLocked: false
      })
    ) {
      return last
    }
    return req
  }

  return req
}

/** Furthest module the learner may enter (first if all locked). */
export function findLastUnlockedModuleId(pyp) {
  const allIds = pyp?.modules?.allIds || []
  if (!allIds.length) return null
  let last = allIds[0]
  for (let i = 0; i < allIds.length; i++) {
    if (isEMpoweredModuleLocked(pyp, allIds[i])) break
    last = allIds[i]
  }
  return last
}
